import type { MaterialValue } from "@rogue/rackbuilder"
import { RACK_COLORS } from "../constants"
import type { RackOptions, PlateOptions, CaveOptions, CTTowerOptions, LatAndLowRowAttachmentSelection, ShelfSelection } from "../RackTypes"
import { findBundleProduct, findCustomOption } from "./product-lookup"
import type { Product, IRogue, BuyRequest, BuyRequestMeta } from '@/js/services/RogueTypes'
import { getBuyRequestMeta } from "@/js/helpers/buy-request"
import { CAVE_OLD_SLINGER_SKU, CAVE_SLINGER_X_SKU } from "../products/cave/utils/caveConstants"


// MARK: Rack Selections
export function getHardwareSelection(rogue: IRogue, buyRequest: BuyRequest) {
  const selectedHardware = findBundleProduct(rogue, buyRequest, 'hardware')?.name || findCustomOption(rogue, buyRequest, 'hardware')

  let hardware: RackOptions['hardware'] = 'zinc'

  if (selectedHardware?.includes('Black')) {
    hardware = 'isonite'
  }

  return hardware
}

export function getFrontSelection(rogue: IRogue, buyRequest: BuyRequest, meta: BuyRequestMeta) {
  const isRML390 = meta.name.includes('RML-390') && !meta.name.includes('RML-390F')
  const isRML3 = meta.name.includes('R-3')
  const isCollegiateHalfRack = meta.name.includes('Collegiate')

  if (isRML390 || isRML3) {
    return undefined
  }

  // RM Collegiate Half Rack uses 'crossmember' as an option name for it's front selection
  const selectedFront = findBundleProduct(rogue, buyRequest, 'pull-up')?.name || findBundleProduct(rogue, buyRequest, 'front')?.name || findCustomOption(rogue, buyRequest, 'pull-up') || findCustomOption(rogue, buyRequest, 'crossmember')
  let front: RackOptions['front'] = 'skinny'

  if (selectedFront?.includes('-431')) {
    front = 'skinny'
  } else if (selectedFront?.includes('-432')) {
    front = 'fat'
  } else if (selectedFront?.includes('Socket')) {
    front = 'socket bar matte matte'
    if (selectedFront?.includes('Stainless') && selectedFront?.includes('Knurled')) {
      front = 'socket bar stainless knurl'
    } else if (selectedFront?.includes('Stainless') && selectedFront?.includes('Smooth')) {
      front = 'socket bar stainless stainless'
    } else if (selectedFront?.includes('Curl')) {
      front = 'socket curl'
    }
  } else if (selectedFront?.includes('XM-433') || (selectedFront?.includes('Fat') && selectedFront?.includes('Skinny'))) {
    front = 'fat skinny'
  } else if (selectedFront?.includes('Crown')) {
    front = 'crown'
  }

  if (isCollegiateHalfRack && selectedFront?.includes('Beam') && !selectedFront?.includes('Crown')) {
    front = 'beam'
  }

  return front
}

export function getMiddleSelection(multiGripAccessory: boolean) {
  let middle: RackOptions['middle'] = ''

  if (multiGripAccessory) {
    middle = 'multi grip'
  } else {
    middle = 'fat skinny'
  }

  return middle
}

export function getBackSelection(rogue: IRogue, buyRequest: BuyRequest, meta: BuyRequestMeta) {
  const isRM = meta.name.includes('RM-3') || meta.name.includes('RM-4') || meta.name.includes('RM-6')
  const isLatAndLowRow = meta.groupSku?.includes('LPLR')
  let back: RackOptions['back'] = ''

  if (isRM) {
    back = 'nameplate'
  }

  let selectedBack = findBundleProduct(rogue, buyRequest, 'rear crossmember')?.name 
    || findCustomOption(rogue, buyRequest, 'rear')

  if (isLatAndLowRow) {
    selectedBack = findBundleProduct(rogue, buyRequest, 'middle crossmember')?.name 
  }

  if (selectedBack?.includes('Name')) {
    back = 'nameplate'
  } else if (selectedBack?.includes('Top Crossmember') || selectedBack?.includes('Beam')) {
    back = 'crossmember top 4'
  } else if (selectedBack?.includes('Fat/Skinny')) {
    back = 'fat skinny'
  }

  return back
}

export function getNamePlateMaterials(rogue: IRogue, buyRequest: BuyRequest): { [key: string]: MaterialValue } | undefined {
  const selectedBack = findBundleProduct(rogue, buyRequest, 'rear crossmember')?.attributes?.nameplate_color as string 
    || findBundleProduct(rogue, buyRequest, 'middle crossmember')?.attributes?.nameplate_color as string

  const namePlateColor = RACK_COLORS[selectedBack]

  if (selectedBack?.includes('Stainless')) {
    return { branding: { material: 'stainless' } }
  } if (selectedBack === 'Textured Black') {
    return { branding: { material: 'powdercoat', color: 'black' }, base: { material: 'mg' } }
  } if (selectedBack === 'Satin Black') {
    return { branding: { material: 'powdercoat' } }
  }

  if ((namePlateColor === 'satinClear')) {
    return { branding: { material: 'satinClear', color: undefined } }
  }

  if ((selectedBack?.includes('Textured') && selectedBack?.includes('Red'))) {
    return { branding: { material: 'mg', color: RACK_COLORS['Rogue Red'] }, base: { material: 'powdercoat' } }
  }

  if (selectedBack && RACK_COLORS[selectedBack]) {
    return { branding: { name: 'metal', material: 'mg', color: namePlateColor } }
  }

  return undefined
}

export function getCrownMaterials(rogue: IRogue, buyRequest: BuyRequest): { [key: string]: MaterialValue } | undefined {
  const selectedCrown = findBundleProduct(rogue, buyRequest, 'pull-up')?.attributes?.beam_color as string
  const crownColor = RACK_COLORS[selectedCrown]

  if (crownColor?.includes('stainless')) {
    return { beam: { material: 'stainless' } }
  }

  if ((crownColor === 'powdercoat')) {
    return { beam: { material: 'powdercoat' } }
  }

  if ((crownColor === 'satinClear')) {
    return { beam: { material: 'satinClear' } }
  }

  if (crownColor) {
    return { beam: { name: 'metal', material: 'mg', color: crownColor } }
  }

  return undefined
}

// Shared by the top/rear and front readers below - only which option's beam_color feeds it differs.
function getCrossmemberMaterialsForBeamColor(beamColor?: string): { [key: string]: MaterialValue } | undefined {
  const crossmemberColor = RACK_COLORS[beamColor as string]

  if (crossmemberColor?.includes('stainless')) {
    return { main: { material: 'stainless' } }
  }

  if ((crossmemberColor === 'powdercoat')) {
    return { main: { material: 'powdercoat' } }
  }

  if ((crossmemberColor === 'satinClear')) {
    return { main: { material: 'satinClear' } }
  }

  if (crossmemberColor) {
    return { main: { name: 'metal', material: 'mg', color: crossmemberColor } }
  }

  return undefined
}

export function getTopCrossmemberMaterials(rogue: IRogue, buyRequest: BuyRequest): { [key: string]: MaterialValue } | undefined {
  const selectedTopCrossmember = findBundleProduct(rogue, buyRequest, 'rear crossmember')?.attributes?.beam_color as string
    || findBundleProduct(rogue, buyRequest, 'middle crossmember')?.attributes?.beam_color as string
    || findBundleProduct(rogue, buyRequest, 'front crossmember')?.attributes?.beam_color as string

  return getCrossmemberMaterialsForBeamColor(selectedTopCrossmember)
}

// Reads only Front Crossmember:, unlike getTopCrossmemberMaterials, which reaches it as a last fallback and so returns the rear's color on any product carrying both rows.
export function getFrontCrossmemberMaterials(rogue: IRogue, buyRequest: BuyRequest): { [key: string]: MaterialValue } | undefined {
  return getCrossmemberMaterialsForBeamColor(findBundleProduct(rogue, buyRequest, 'front crossmember')?.attributes?.beam_color as string)
}

export function getJCupSelection(rogue: IRogue, buyRequest: BuyRequest, jcupsIncluded = false) {
  const selectedJCup = findBundleProduct(rogue, buyRequest, 'j-cup', true)?.name || findCustomOption(rogue, buyRequest, 'j-cups')
  let jcup: RackOptions['jcup'] = undefined

  if (selectedJCup?.includes('Ghost')) {
    jcup = 'ghost-jcup'
  } else if (selectedJCup?.includes('2') && selectedJCup?.includes('Sandwich')) {
    jcup = 'sandwich jcup 2'
  } else if (selectedJCup?.includes('1') && selectedJCup?.includes('Sandwich')) {
    jcup = 'sandwich jcup 1'
  } else if (selectedJCup?.includes('Standard') || jcupsIncluded) {
    jcup = 'jcup'
  }

  return jcup
}

export function getSafetySelection(rogue: IRogue, buyRequest: BuyRequest, meta: BuyRequestMeta, strapSafeties: boolean) {
  const selectedSafeties = findBundleProduct(rogue, buyRequest, 'safeties', true)?.name || findCustomOption(rogue, buyRequest, 'safety')

  let safeties: RackOptions['safeties'] = 'pipe-safety'
  const isRML3 = meta.name.includes('R-3')
  const isRML390F = meta.name.includes('RML-390F')

  if (selectedSafeties?.includes('Flip')) {
    safeties = 'flipdown-safety'
  } else if (selectedSafeties?.includes('Strap') || strapSafeties) {
    safeties = 'strap-safety'
  } else if (isRML3 || isRML390F) {
    safeties = 'pipe-safety'
  } else if (!selectedSafeties) {
    safeties = ''
  }

  return safeties
}

export function getFxtSafetySelection(rogue: IRogue, buyRequest: BuyRequest) {
  const selectedSafeties = findBundleProduct(rogue, buyRequest, 'safeties', true)?.name

  let safeties: RackOptions['safeties'] = 'pipe-safety'

  if (selectedSafeties?.includes('Pipe')) {
    safeties = 'pipe-safety'
  } else if (selectedSafeties?.includes('Flip')) {
    safeties = 'flipdown-safety'
  } else if (selectedSafeties?.includes('Strap')) {
    safeties = 'strap-safety'
  } else if (!selectedSafeties) {
    safeties = ''
  }

  return safeties
}



export function getFrontStabilizerSelection(rogue: IRogue, buyRequest: BuyRequest, meta: BuyRequestMeta) {
  const selectedStabilizer = findCustomOption(rogue, buyRequest, 'stabilizer')
  const is490 = meta.name.includes('RML-490')

  // This is needed because AU returns 'No Stabilizer' while rest of stores return 'None'
  if (selectedStabilizer?.includes('No Stabilizer') || is490) {
    return false
  }

  if (selectedStabilizer?.includes('Stabilizer')) {
    return true
  }

  return false
}

export function getBackStabilizerSelection(rogue: IRogue, buyRequest: BuyRequest, meta: BuyRequestMeta) {
  const backStabilizerOption = findBundleProduct(rogue, buyRequest, 'Stabilizer', true)?.name

  const is490 = meta.name.includes('RML-490')

  // AU returns 'No Stabilizer' instead of 'None'
  if (backStabilizerOption?.includes('Stabilizer') || (is490 && backStabilizerOption?.includes('Stabilizer') && !backStabilizerOption?.includes('No Stabilizer'))) {
    return true
  }

  return false
}

export function getHeightSelection(rack: Product) {
  // The heights should be '80' | '90' | '100' | '108'
  return rack.attributes.rack_height?.toString()?.replace('"', '') || '90'
}

export function parseRackDepth(rackDepth: string | undefined): RackOptions['depth'] {
  if (rackDepth?.includes('43')) return '4'
  if (rackDepth?.includes('30')) return '3'
  if (rackDepth?.includes('24')) return '2'
  if (rackDepth?.includes('17') || rackDepth?.includes('14')) return '1'

  return '2'
}

export function getDepthSelection(rack: Product, meta: BuyRequestMeta) {
  const selectedDepth = rack.attributes.rack_depth
  const is590C = meta.name.includes('RML-590C')

  let depth: RackOptions['depth'] = '4'

  if (selectedDepth === '17"' || selectedDepth === '14"') {
    depth = '1'
  } else if (selectedDepth === '24"') {
    depth = '2'
  } else if (selectedDepth === '30"' || is590C) {
    depth = '3'
  } else if (selectedDepth === '70"') {
    depth = '7'
  }

  return depth
}

export function getBenchSelection(rogue: IRogue, buyRequest: BuyRequest) {
  const selectedBench = findBundleProduct(rogue, buyRequest, 'bench', true)?.name
  let bench: RackOptions['bench']

  if (selectedBench?.includes('Flat')) {
    bench = 'flat'
  } else if (selectedBench?.includes('Adjustable')) {
    bench = 'adjustable'
  }

  return bench
}

export function getPlateSelection(rogue: IRogue, buyRequest: BuyRequest) {
  const selectedPlates = findBundleProduct(rogue, buyRequest, 'Add plates', true)?.name

  let plates

  if (selectedPlates) {
    plates = [
      { line: selectedPlates?.includes('HG') ? 'hg' : 'echo', weight: '45' },
      { line: selectedPlates?.includes('HG') ? 'hg' : 'echo', weight: '35' },
      { line: selectedPlates?.includes('HG') ? 'hg' : 'echo', weight: '25' },
      { line: selectedPlates?.includes('HG') ? 'hg' : 'echo', weight: '15' },
      { line: selectedPlates?.includes('HG') ? 'hg' : 'echo', weight: '10' },
    ] as PlateOptions
  }

  if (plates && selectedPlates?.includes('230')) {
    // Remove the 15s from the 230lb set
    plates = plates.filter(p => p.weight !== '15')
  }

  return plates
}

export function getBandPegSelection(rogue: IRogue, buyRequest: BuyRequest) {
  const selectedPegs = findBundleProduct(rogue, buyRequest, 'band peg', true)?.name

  let pegs: RackOptions['bandPegs']

  if (selectedPegs?.includes('Zinc')) {
    pegs = 'zinc'
  } else if (selectedPegs?.includes('Matte')) {
    pegs = 'matte'
  }

  return pegs
}

export function getPlateStorageSelection(rogue: IRogue, buyRequest: BuyRequest) {
  const selectedPlateStorage = findBundleProduct(rogue, buyRequest, 'plate storage', true)?.name

  let storage: RackOptions['plateStorage']

  if (selectedPlateStorage?.includes('8')) {
    storage = '8 long'
  } else if (selectedPlateStorage?.includes('4 Long')) {
    storage = '4 long 4 short'
  } else if (selectedPlateStorage?.includes('6 Long') && selectedPlateStorage?.includes('4 Short')) {
    storage = '6 long 4 short'
  } else if (selectedPlateStorage?.includes('6 Long') && selectedPlateStorage?.includes('2 Short')) {
    storage = '6 long 2 short'
  }

  return storage
}

export function getCavePlateStorageSelection(rogue: IRogue, buyRequest: BuyRequest) {
  const selectedPlateStorage = findBundleProduct(rogue, buyRequest, 'plate storage', true)?.name.toLowerCase()

  let storage: RackOptions['plateStorage']

  if (selectedPlateStorage?.includes('3 short') && selectedPlateStorage?.includes('2 long')) {
    storage = '3 short 2 long'
  } else if (selectedPlateStorage?.includes('4 long')) {
    storage = '4 long'
  } else if (selectedPlateStorage?.includes('3 short') && !selectedPlateStorage?.includes('2 long')) {
    storage = '3 short'
  } else if (selectedPlateStorage?.includes('2 long') && !selectedPlateStorage?.includes('3 short')) {
    storage = '2 long'
  }

  return storage
}

// MARK: Monster Mass Storage & Mass Storage
export function getShelfSelection(selectionName: string | undefined): string | undefined {
  if (!selectionName) return selectionName

  if (selectionName?.toLowerCase()?.includes('dumbbell')) {
    selectionName = 'dumbbell shelf'
  } else if (selectionName?.toLowerCase()?.includes('kettlebell')) {
    selectionName = 'kettlebell shelf'
  } else if (selectionName?.toLowerCase()?.includes('bumper')) {
    selectionName = 'bumper'
  } else if (
    (selectionName?.toLowerCase()?.includes('crossmember') && !selectionName?.toLowerCase()?.includes('parallel'))
    || selectionName?.toLowerCase() === 'rail'
  ) {
    // Second clause: the bolt counter runs this again on an already-resolved 'rail', so keep returning 'rail' — needed for bolt-counting correctness.
    selectionName = 'rail'
  } else {
    selectionName = 'medball shelf'
  }

  return selectionName
}

//  The Cave 2.0 Shelf Selectons Keywords
const SHELF_KEYWORDS = [
  { keyword: 'liner', meshName: 'liner' },
  { keyword: 'dumbbell', meshName: 'dumbbell shelf' },
  { keyword: 'kettlebell', meshName: 'kettlebell shelf' },
  { keyword: 'dividers', meshName: 'bumper' },
  { keyword: 'medball', meshName: 'medball shelf' },
  { keyword: 'panel', meshName: 'panel' },
] as const

//  The Cave 2.0 Shelf Selectons Helper
export function normalizeShelfName(selectionName: string | undefined): string | undefined {
  if (!selectionName) return undefined

  // Coerced because the name comes straight off a catalog response: hybrid-rules now reads this on the PDP, where a non-string would blank the whole bundle option rather than just drop a mesh.
  const lower = String(selectionName).toLowerCase()
  const match = SHELF_KEYWORDS.find(({ keyword }) => lower.includes(keyword))

  return match?.meshName ?? 'medball shelf'
}

//  The Cave 2.0 Shelf Selectons
export function getShelfSelections(rogue: IRogue, sideBuyRequest: BuyRequest) {
  const config: ShelfSelection = {}

  for (let i = 1; i <= 5; i++) {
    const key = `shelf${i}` as keyof ShelfSelection
    const product = findBundleProduct(rogue, sideBuyRequest, `shelf ${i}`, true)
    config[key] = normalizeShelfName(product?.name)
  }

  return config
}


export function getCornerSelection(selectionName: string | undefined): string | undefined {
  if (!selectionName) return selectionName

  if (selectionName?.toLowerCase()?.includes('2 pack')) {
    selectionName = '2'
  } else if (selectionName?.toLowerCase()?.includes('3 pack')) {
    selectionName = '3'
  } else if (selectionName?.toLowerCase()?.includes('4 pack')) {
    selectionName = '4'
  } else {
    selectionName = '1'
  }

  return selectionName
}

export function getHangerSelection(selectionName: string | undefined): string | undefined {
  if (!selectionName) return selectionName

  if (selectionName?.toLowerCase()?.includes('pair')) {
    selectionName = '2'
  } else if (selectionName?.toLowerCase()?.includes('4 pack')) {
    selectionName = '4'
  } else {
    selectionName = '0'
  }

  return selectionName
}

// MARK: Cave Selections
export function getPanelWidthSelection(rack: Product): string {
  return rack.attributes?.mass_width?.toString()?.replace('"', '') || '43'
}

export function getFoldBackDepth(rack: Product) {
  const foldBackDepth = rack.attributes.rack_depth as string
  let rackDepth: CaveOptions['foldBackDepth'] = '21'

  if (foldBackDepth.includes('21.5"')) {
    rackDepth = '21'
  } else if (foldBackDepth.includes('24"')) {
    rackDepth = '24'
  } else if (foldBackDepth.includes('30"')) {
    rackDepth = '30'
  } else if (foldBackDepth.includes('41.5"')) {
    rackDepth = '41'
  }

  return rackDepth
}

export function getCableSystemSelection(cableSystem: Product | undefined) {
  const cableSystemName = cableSystem?.name?.toString()?.toLowerCase()
  const sku = cableSystem?.sku

  let cableSelection: CaveOptions['leftCableSystem'] = undefined

  if (cableSystemName?.includes('ct-1 cable tower')) {
    cableSelection = 'ct'
  } else if (cableSystemName?.includes('lp-2')) {
    cableSelection = 'lp'
  } else if (sku === CAVE_SLINGER_X_SKU || cableSystemName?.includes('slinger x')) {
    cableSelection = cableSystemName?.includes('300') ? 'slinger x 300' : 'slinger x 250'
  } else if (sku === CAVE_OLD_SLINGER_SKU || cableSystemName?.includes('slinger')) {
    // Falls back to legacy for any name/SKU that says "slinger" but not "slinger x" — never the new builder.
    cableSelection = 'slinger'
  } else if (cableSystemName?.includes('indy')) {
    cableSelection = 'indy'
  }

  if (cableSystemName?.includes('ctm') && cableSystemName?.includes('250')) {
    cableSelection = 'ctm 250'
  } else if (cableSystemName?.includes('ctm') && cableSystemName?.includes('300')) {
    cableSelection = 'ctm 300'
  }

  if (cableSystemName?.includes('indy') && cableSystemName?.includes('250')) {
    cableSelection = 'indy 250'
  } else if (cableSystemName?.includes('indy') && cableSystemName?.includes('300')) {
    cableSelection = 'indy 300'
  }

  return cableSelection
}

// MARK: CTM1
export function getTrolleySelection(rogue: IRogue, buyRequest: BuyRequest): CTTowerOptions['trolleySelection'] {
  const selectedTrolley = findBundleProduct(rogue, buyRequest, 'trolley', true)?.name

  let trolleySelection: CTTowerOptions['trolleySelection']

  if (selectedTrolley?.toLowerCase()?.includes('tr-2d')) {
    trolleySelection = 'tricep'
  } else if (selectedTrolley?.toLowerCase()?.includes('push down')) {
    trolleySelection = 'pushdown'
  } else if (selectedTrolley?.toLowerCase()?.includes('leather')) {
    trolleySelection = 'leather'
  } else if (selectedTrolley?.toLowerCase()?.includes('rogue tricep strap')) {
    trolleySelection = 'nylon'
  } else if (selectedTrolley?.toLowerCase()?.includes('single')) {
    trolleySelection = 'handle'
  } else if (selectedTrolley?.toLowerCase()?.includes('v-grip')) {
    trolleySelection = 'v grip'
  }

  return trolleySelection
}

export function getAttachmentSelection(rogue: IRogue, buyRequest: BuyRequest, bundleTitle: string): LatAndLowRowAttachmentSelection {
  const selectedLatBar = findBundleProduct(rogue, buyRequest, bundleTitle, true)?.name.toLowerCase()

  

  let latBarSelection: LatAndLowRowAttachmentSelection

  if (selectedLatBar?.includes('1x')) {
    latBarSelection = 'x'
  } else if (selectedLatBar?.includes('close')) {
    latBarSelection = 'close'
  } else if (selectedLatBar?.includes('cg') && selectedLatBar?.toLowerCase()?.includes('triangle')) {
    latBarSelection = 'cg triangle'
  } else if (selectedLatBar?.includes('multi')) {
    latBarSelection = 'multi'
  } else if (selectedLatBar?.toLowerCase()?.includes('triangle')) {
    latBarSelection = 'triangle'
  } else if (selectedLatBar?.toLowerCase()?.includes('lat')) {
    latBarSelection = 'lat bar'
  } else if (selectedLatBar?.toLowerCase()?.includes('push down')) {
    latBarSelection = 'pushdown'
  } else if (selectedLatBar?.includes('single handle')) {
    latBarSelection = 'handle'
  } else if (selectedLatBar?.includes('rogue tricep strap')) {
    latBarSelection = 'nylon'
  } else if (selectedLatBar?.includes('tricep dual')) {
    latBarSelection = 'tricep'
  } else if (selectedLatBar?.includes('v-grip')) {
    latBarSelection = 'v grip'
  }

  return latBarSelection
}

function getLatPulldownMaterial(selectedLatBar: string | undefined) {
  if (!selectedLatBar) return {}

  if (selectedLatBar.includes('Stainless')) {
    return {
      bar: {
        material : 'knurl',
        color    : '#888888',
        config   : { mapRepeat: 0.6, metalness: 0, roughness: 0.85 },
      },
    }
  }

  return {
    bar: {
      material : 'knurl',
      color    : '#1A1A1A',
      config   : { mapRepeat: 0.6, metalness: 0, roughness: 0.85 },
    },
    metal: { material: 'mg', color: '#1A1A1A' },
  }
}

export function getCommonAccessorySelections(
  rogue: IRogue,
  buyRequest: BuyRequest,
  materials: { [key: string]: { [key: string]: MaterialValue } }
) {
  const meta = getBuyRequestMeta(buyRequest, rogue)
  const isCave = meta.groupSku?.includes('CAVE')
  const isSlingerX = meta.groupSku?.includes('SLINGER')
  const latBarKey = isCave || isSlingerX ? 'lat' : 'lat bar'

  const trolleySelection = getTrolleySelection(rogue, buyRequest)
  const lowRowSelection = getAttachmentSelection(rogue, buyRequest, 'low')
  const latBarSelection = getAttachmentSelection(rogue, buyRequest, latBarKey)

  const selectedLatBar = findBundleProduct(rogue, buyRequest, latBarKey, true)?.name

  materials.latpulldown = getLatPulldownMaterial(selectedLatBar) as { [key: string]: MaterialValue }

  return {
    trolleySelection,
    lowRowSelection,
    latBarSelection,
  }
}

export function getCommonCaveSelections(
  rogue: IRogue,
  buyRequest: BuyRequest,
  isLeft: boolean,
  materials: any
) {
  // Ensure left/right objects exist
  materials.left = materials.left || {}
  materials.right = materials.right || {}
  const side = isLeft ? materials.left : materials.right

  const lowRowSelection = getAttachmentSelection(rogue, buyRequest, 'low')
  const latBarSelection = getAttachmentSelection(rogue, buyRequest, 'lat')
  const trolleySelection = getTrolleySelection(rogue, buyRequest)

  const selectedLatBar = findBundleProduct(rogue, buyRequest, 'lat', true)?.name

  // Merge into side-specific latpulldown
  side.latpulldown = { ...(side.latpulldown || {}), ...getLatPulldownMaterial(selectedLatBar) }

  return {
    lowRowSelection,
    latBarSelection,
    trolleySelection,
  }
}
